/**
 * Turn whatever MediaRecorder produced (webm/Opus in Chrome, mp4/AAC in
 * Safari, ogg in Firefox) into 16 kHz mono 16-bit WAV.
 *
 * WAV because it is the one format every link in the chain takes as-is: the
 * agent's transcriber (Gemini) does not list webm, and Discord plays WAV
 * inline. 16 kHz mono is plenty for speech and costs 32 KB a second, so a
 * 4-minute note stays under Discord's 8 MB file limit.
 */
export const WAV_SAMPLE_RATE = 16_000;

export async function toSpeechWav(recording: Blob): Promise<{ wav: Blob; durationSecs: number }> {
  const ctx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(await recording.arrayBuffer());
  } finally {
    void ctx.close();
  }
  const frames = Math.max(1, Math.ceil(decoded.duration * WAV_SAMPLE_RATE));
  // A one-channel destination downmixes stereo input on the way through.
  const offline = new OfflineAudioContext(1, frames, WAV_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  return {
    wav: encodeWav(rendered.getChannelData(0), WAV_SAMPLE_RATE),
    durationSecs: decoded.duration,
  };
}

export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, "data");
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buffer], { type: "audio/wav" });
}
